import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { cleanActions, getActions, setAcrhivedActionsOffset } from '@/store/actions/actions';
import { selectUser } from '@/store/selectors/user';

import useLanguage from '@/components/common/hooks/useLanguage';
import useToggle from '@/components/common/hooks/useToggle';
import ItemHeader from '@/components/actions/ItemHeader';
import EmptyCatalog from '@/components/actions/EmptyCatalog';
import Catalog from '@/components/teamActions/Catalog';
import Loader from '@/components/common/Loader';
import Navigation from '@/components/Navigation';
import { ActionLoaderState } from '@/store/reducers/actions';
import { selectActionLoaderState, selectArchivedActions, selectArchivedActionsOffset } from '@/store/selectors/actions';
import { getCustomers, cleanCustomers } from '@/store/actions/customers';
import { getProjects, cleanProjects } from '@/store/actions/projects';
import { getUsersWithManagers, cleanUsers } from '@/store/actions/users';
import LoaderDots from '@/components/common/LoaderDots';
import Empty from '@/components/Empty';

const TeamActionsArchive: React.FC = () => {
  const [isFilterActive, toggleIsFilterActive] = useToggle();
  const [isMounted, setIsMounted] = useState(false);
  const [isPaginating, setIsPaginating] = useState(false);
  const loader = useRef(null);
  const dispatch = useDispatch();
  const offset = useSelector(selectArchivedActionsOffset);
  const actionLoader = useSelector(selectActionLoaderState);
  const [page, setPage] = useState<number>(offset);
  const history = useHistory();
  const actions = useSelector(selectArchivedActions);
  const user = useSelector(selectUser);

  // Derived in render, never copied into state by an effect: an effect runs with
  // the actions of the render that scheduled it, and the copy would outlive the
  // wipe that follows.
  const filteredActions = useMemo(() => (actions && isFilterActive
    ? actions.filter((action) => (
      action.type === 'team' && action.assign.includes(user!._id)
    ))
    : actions), [isFilterActive, actions]);

  const [{ common }] = useLanguage();

  // A layout effect, so the stored copies render before the first paint -- see
  // store/responses.ts.
  useLayoutEffect(() => {
    const getInfo = async () => {
      dispatch(getActions(user?.role));
      dispatch(getUsersWithManagers());
      dispatch(getProjects());
      dispatch(getCustomers());
    };
    if (!actions) {
      getInfo();
    }
    return () => {
      if (!history.location.pathname.includes('actions')) {
        dispatch(setAcrhivedActionsOffset(0));
        dispatch(cleanActions());
        dispatch(cleanUsers());
        dispatch(cleanProjects());
        dispatch(cleanCustomers());
      }
    };
  }, []);

  const handleObserver = useCallback((entries) => {
    const target = entries[0];
    if (target.isIntersecting) {
      setPage((prev) => prev + 10);
    }
  }, []);

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: '20px',
      threshold: 0,
      delay: 100,
    };
    if (isPaginating) {
      const observer = new IntersectionObserver(handleObserver, option);
      if (loader.current) observer.observe(loader.current!);
    }
  }, [isPaginating]);

  useEffect(() => {
    if (isMounted && user) {
      dispatch(getActions(user.role, page));
      dispatch(setAcrhivedActionsOffset(page));
    } else {
      setIsMounted(true);
    }
  }, [page]);

  useEffect(() => {
    if (actions) {
      setIsPaginating(true);
    }
  }, [actions]);

  const handleGoBack = () => {
    history.goBack();
  };

  return (
    <>
      <ItemHeader
        headerName={common.archive}
        toggleFilter={toggleIsFilterActive}
        isFilterActive={isFilterActive}
        goBack={handleGoBack}
      />
      <Empty height="72px" />
      {!filteredActions || !actions
        ? <Loader scale="0.5" />
        : (
          <ItemsContainer className="scrollbar">
            {filteredActions.length ? (
              <Catalog
                pageType="archived"
                actions={filteredActions}
              />
            ) : (
              <EmptyCatalog />
            )}
          </ItemsContainer>
        )}
      {actionLoader === ActionLoaderState.LOADING && (
      <div style={{ marginTop: -85 }}>
        <LoaderDots />
        <div style={{ paddingBottom: 74 }} />
      </div>
      )}
      <div ref={loader} />
      <Empty height="64px" />
      <Navigation path="actions" />
    </>
  );
};

const ItemsContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  position: relative;
  padding: 16px 0;
  overflow-y: auto;
  flex: 1;
`;

export default TeamActionsArchive;
